import { PatchBetrokkenTrein, PostBetrokkenTrein, usePatchBetrokkenTrein, usePostBetrokkenTrein } from "@/entities/betrokken-treinen";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    ErrorDisplay,
    Form,
    FormMessage,
    SingleToggleGroup,
    ToggleGroupOption,
} from "@/shared/components";
import { optionalNumberString, requiredNumberString } from "@/shared/utils/zod-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { PropsWithChildren, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { useValidateDuplicateTrain } from "../../hooks/useValidateDuplicateTrain";
import { createMaterieel } from "../shared/utils";
import { AddTreinFormContent } from "./add-trein-form-content";
import { SpoorwebTreinen } from "./spoorweb-treinen";

type Tab = "spoorweb" | "handmatig";

const tabOptions: ToggleGroupOption<Tab>[] = [
    { value: "spoorweb", label: "Spoorweb" },
    { value: "handmatig", label: "Handmatig" },
];

const addTrainSchema = z
    .object({
        treinId: z.string().optional(), // available for PATCH, but not for POST
        treintype: z.string().trim().nonempty({
            message: "Treintype is verplicht.",
        }),
        treinnummer: requiredNumberString("Treinnummer is verplicht"),
        aantalReizigers: optionalNumberString(),
        materieelAanduiding: z.string().trim().optional(),
        materieelAantalBakken: optionalNumberString(),
    })
    .refine(
        (data) => {
            const hasAanduiding = !!data.materieelAanduiding;
            const hasAantalBakken = !!data.materieelAantalBakken;

            // Both must be filled or both must be empty
            return hasAanduiding === hasAantalBakken;
        },
        {
            message: "Beide velden (materieeltype en aantal bakken) moeten ingevuld zijn of beide leeg gelaten worden",
            path: ["materieelAanduiding"],
        }
    );

const createPostBetrokkenTreinPayload = (
    { materieelAanduiding, materieelAantalBakken, treintype, treinnummer, aantalReizigers }: AddTreinFormData,
    dossierId: string
): PostBetrokkenTrein => {
    const materieel = createMaterieel(materieelAanduiding, materieelAantalBakken);

    return {
        dossierId,
        treinType: treintype,
        treinNummer: Number(treinnummer),
        ...(aantalReizigers && { aantalReizigers: Number(aantalReizigers) }), // avoid empty string becoming 0
        ...(materieel && { materieel }),
    };
};

const createPatchBetrokkenTreinPayload = (
    { materieelAanduiding, materieelAantalBakken }: AddTreinFormData,
    dossierId: string,
    treinId: string
): PatchBetrokkenTrein => {
    const materieel = createMaterieel(materieelAanduiding, materieelAantalBakken);

    return {
        dossierId,
        treinId,
        isRelevant: true,
        ...(materieel && { materieel }),
    };
};

export type AddTreinFormData = z.infer<typeof addTrainSchema>;

export function AddBetrokkenTreinDialog({ dossierNummer, children }: PropsWithChildren<{ dossierNummer: string }>) {
    const [open, setOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<Tab>("spoorweb");

    const form = useForm<AddTreinFormData>({
        resolver: zodResolver(addTrainSchema),
        defaultValues: {
            materieelAanduiding: "",
            materieelAantalBakken: "",
            treinId: "",
            treinnummer: "",
            treintype: "",
            aantalReizigers: "",
        },
    });

    const validateDuplicateTrain = useValidateDuplicateTrain(dossierNummer);

    const postTrainMutation = usePostBetrokkenTrein();
    const patchTrainMutation = usePatchBetrokkenTrein();

    const treinId = form.watch("treinId");

    function handleTypeChange(type: Tab) {
        form.reset();
        setSelectedType(type);
    }

    function handleOpenChange(isOpen: boolean) {
        if (!isOpen) {
            form.reset();
            setSelectedType("spoorweb");
            patchTrainMutation.reset();
            postTrainMutation.reset();
        }
        setOpen(isOpen);
    }

    function onSubmit(data: AddTreinFormData) {
        const { isDuplicate, duplicateSource } = validateDuplicateTrain(Number(data.treinnummer), Number(data.treinId));
        if (isDuplicate) {
            form.setError("treinnummer", {
                message:
                    duplicateSource === "dossier"
                        ? "Dit treinnummer bestaat al in het dossier"
                        : "Selecteer deze trein in het Spoorweb-tabblad",
            });
            return;
        }
        const mutateOptions = {
            onSuccess: () => handleOpenChange(false),
        };
        if (selectedType === "handmatig") {
            postTrainMutation.mutate(createPostBetrokkenTreinPayload(data, dossierNummer), mutateOptions);
        } else {
            patchTrainMutation.mutate(createPatchBetrokkenTreinPayload(data, dossierNummer, treinId!), mutateOptions);
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContainer>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogHeader>
                            <DialogTitle>Trein toevoegen</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-4">
                            {patchTrainMutation.error && <ErrorDisplay error={patchTrainMutation.error} />}
                            {postTrainMutation.error && <ErrorDisplay error={postTrainMutation.error} />}
                            {(selectedType === "handmatig" || !treinId) && (
                                <SingleToggleGroup
                                    options={tabOptions}
                                    value={selectedType}
                                    onValueChange={handleTypeChange}
                                    className="w-fit"
                                />
                            )}
                            {selectedType === "spoorweb" && !treinId && <SpoorwebTreinen dossierNummer={dossierNummer} form={form} />}
                            {(selectedType === "handmatig" || treinId) && <AddTreinFormContent showBack={selectedType === "spoorweb"} />}
                            {form.formState.errors.root && <FormMessage>{form.formState.errors.root.message}</FormMessage>}
                        </DialogBody>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={() => {
                                    handleOpenChange(false);
                                }}
                            >
                                Annuleren
                            </Button>
                            {(selectedType === "handmatig" || treinId) && (
                                <Button type="submit" variant="primary" disabled={patchTrainMutation.isPending}>
                                    Toevoegen
                                </Button>
                            )}
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
